import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../../store";
import {
  getDebtorClaimsEligibleForPostpone,
  hasSelectedDebtorClaims,
} from "../../../../store/Selectors";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import StepZilla from "react-stepzilla";
import { Step } from "../ConversationSupervisorStep";
import {
  PostponeCode,
  RegisterPostponementConversationAction,
  SelectedDebtorClaim,
} from "../../../../api/client";
import { actionCreators as conversationSupervisorAction } from "../../../../store/ConversationSupervisorStore";
import { actionCreators as DebtorClaimsStoreActionCreators } from "../../../../store/DebtorClaimsStore";
import { useAppDispatch } from "../../../../hooks";

interface InjectedProps {
  jumpToStep?: typeof StepZilla.jumpToStep;
}

const Postpone = ({ jumpToStep }: InjectedProps) => {
  const dispatch = useAppDispatch();

  const hasSelectedClaims = useSelector((state: ApplicationState) =>
    hasSelectedDebtorClaims(state.debtorClaims)
  );

  const debtorClaimsEligibleForPostpone = useSelector(
    (state: ApplicationState) => getDebtorClaimsEligibleForPostpone(state)
  );
  const selectedDebtorClaims = useSelector(
    (state: ApplicationState) => state.debtorClaims.selectedDebtorClaims
  );

  const [noteText, setNoteText] = useState("");
  const [postponeCode, setPostponeCode] = useState<PostponeCode>(
    PostponeCode.TwoWeeks
  );

  const handleTextFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNoteText(event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const action: RegisterPostponementConversationAction = {
      reason: noteText,
      postponeCode: postponeCode,
      discriminator: "RegisterPostponementConversationAction",
      selectedDebtorClaims: selectedDebtorClaims,
    };

    const addConversationAction = conversationSupervisorAction.addConversationAction;
    dispatch(addConversationAction(action));

    if (jumpToStep) {
      jumpToStep(Step.Summary);
    }
  };

  useEffect(() => {
    if (!selectedDebtorClaims || selectedDebtorClaims.length === 0) return;

    const toggleDebtorClaimSelection = DebtorClaimsStoreActionCreators.toggleDebtorClaimSelection;

    const notValidClaims: SelectedDebtorClaim[] = selectedDebtorClaims.filter(
      (postponeClaim) =>
        !debtorClaimsEligibleForPostpone.some(
          (eligibleClaim) => eligibleClaim.number === postponeClaim.number
        )
    );

    notValidClaims.map((claim) =>
      dispatch(
        toggleDebtorClaimSelection({
          providerSystem: claim.providerSystem,
          number: claim.number!.toString(),
          type: claim.type!.toString(),
          externalReferenceId: claim.externalReferenceId!,
        })
      )
    );
  }, [debtorClaimsEligibleForPostpone, dispatch, selectedDebtorClaims]);

  const canPostpone =
    noteText.trim() !== "" &&
    postponeCode &&
    debtorClaimsEligibleForPostpone.length > 0 &&
    hasSelectedClaims &&
    selectedDebtorClaims.every((selectedClaim) =>
      debtorClaimsEligibleForPostpone.some(
        (eligibleClaim) => selectedClaim.number === eligibleClaim.number
      )
    );

  return (
    <React.Fragment>
      <Select
        fullWidth
        onChange={(event) =>
          setPostponeCode(event.target.value as PostponeCode)
        }
        displayEmpty
        sx={{ my: 3 }}
        value={postponeCode || ""}
      >
        <MenuItem value="" disabled>
          <em>Velg hvor lenge du vil utsette</em>
        </MenuItem>
        <MenuItem value={PostponeCode.TwoWeeks}>
          Kode 1258 - Avventer frist 14 dager
        </MenuItem>
        <MenuItem value={PostponeCode.OneMonth}>
          Kode 1259 - Avventer frist 30 dager
        </MenuItem>
      </Select>

      <TextField
        fullWidth
        multiline
        required
        rows={4}
        value={noteText}
        onChange={handleTextFieldChange}
        label="Begrunnelse for å utsette"
        placeholder="Fyll inn en kommentar..."
        error={!noteText.trim()}
        sx={{ mb: 3 }}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          onClick={() => jumpToStep && jumpToStep(Step.Start)}
        >
          Avbryt
        </Button>

        <Button
          variant="contained"
          id="submit"
          onClick={handleSubmit}
          disabled={!canPostpone}
          sx={{ marginLeft: '8px' }}
        >
          OK
        </Button>
      </Box>
    </React.Fragment>
  );
};

export default Postpone;
